/**
 * Per-article "scan this first" prompts.
 *
 * Why this exists: Supabase data shows 91% of installs never run a scan
 * (15 scanners out of 213 signups). The diagnosis is that readers land
 * on a blog post, get their answer, install the app, then sit on the
 * camera screen with nothing specific to point at and bounce.
 *
 * The fix is to tell them, before they install, what they should scan
 * first, tied to the topic of the article they just finished. A reader
 * of the K-Cups article installs with an intent: "scan my K-Cup pack."
 * That single difference between "I have an app installed" and "I have
 * an app installed and I know what I'm scanning right now" is the
 * biggest install→first-scan lever available on the web side.
 *
 * Coverage strategy: hand-write entries for the highest-traffic posts
 * (from GA4 30-day pageviews, plus posts with strong GSC impressions
 * but weak CTR, those are leaking the most install-intent). Any post
 * without an entry falls back to the existing topic-aware bottom CTA,
 * which is already shipped in BlogPostLayout.
 */
export interface FirstScanPrompt {
  /** What the reader should physically pick up and point the camera at. */
  product: string
  /** Specific brand examples that match the article. Comma-separated. */
  brands: string
  /** One sentence on why scanning this specific item tells them something
   *  the article couldn't: cross-pack variance, hidden reformulations,
   *  pack-size differences, etc. */
  payoff: string
  /** Optional override for the scan-here CTA label. Defaults to a
   *  conversion-tested generic. */
  ctaLabel?: string
}

export const FIRST_SCAN_PROMPTS: Record<string, FirstScanPrompt> = {
  // ── August 29–September 4 search-demand series ─────────────────────────
  'does-essentia-water-have-microplastics': {
    product: 'your Essentia bottle and the water format you use most days',
    brands: 'Essentia, home RO, Brita, ZeroWater, glass and stainless bottles',
    payoff: 'Essentia has not published a final-package particle count. Scan the PET bottle beside a filtered reusable setup to compare the documented contact conditions you can actually change.',
  },
  'does-vaseline-have-microplastics': {
    product: 'your exact Vaseline jar, lotion, stick or lip product',
    brands: 'Original Healing Jelly, Cocoa Butter, body lotion, balm stick, lip therapy',
    payoff: 'Original Jelly has one ingredient, but the variants do not. Scan the back label so the one-ingredient verdict is never applied to the wrong formula.',
  },
  'does-colgate-toothpaste-have-microplastics': {
    product: 'the ingredient panel on your toothpaste tube',
    brands: 'Colgate Total, Optic White, Max Fresh, Sensitive, kids toothpaste',
    payoff: 'Colgate formulas vary by product and country. Scan the tube in your hand to screen that formula instead of relying on a brand-wide answer.',
  },
  'are-nylon-cooking-utensils-plastic': {
    product: 'your most-used spatula or cooking spoon',
    brands: 'nylon or PA, polypropylene, silicone, wood, stainless steel',
    payoff: 'A black color does not identify a material. Scan the molded mark and condition to compare your hot-food tool with a functional wood or steel swap.',
  },
  'is-polyester-clothing-plastic': {
    product: 'the care and fibre label on your most-washed garment',
    brands: 'polyester activewear, fleece, school uniforms, cotton-poly blends',
    payoff: 'The percentage and garment type matter more than the front label. Scan the fibre blend to prioritize the synthetic item that reaches the washer most often.',
  },
  'does-parchment-paper-have-plastic': {
    product: 'your roll or box of cooking paper',
    brands: 'Reynolds parchment, store-brand parchment, freezer paper, wax paper',
    payoff: 'Parchment, freezer paper and wax paper use different surfaces. Scan the exact box to check its coating and temperature limit before it reaches the oven.',
  },
  'does-evian-have-microplastics': {
    product: 'the Evian package you actually buy',
    brands: 'Evian PET, Evian glass, filtered tap, glass and stainless bottles',
    payoff: 'The 2018 study tested old lots, not your bottle. Scan the current package to compare its documented material and use conditions with a reusable format.',
  },

  // ── High-traffic pages missing a product-specific first action ─────────
  'chewing-gum-brands-ranked-microplastics': {
    product: 'the gum pack in your bag or car',
    brands: 'Trident, Extra, Orbit, 5 Gum, Simply Gum, Glee, PUR',
    payoff: 'Gum base formulas differ even within a brand. Scan the exact pack to compare its declared base and packaging with a lower-synthetic alternative.',
  },
  'does-graza-have-microplastics': {
    product: 'your olive-oil bottle',
    brands: 'Graza squeeze bottle, glass-bottled EVOO, metal tin',
    payoff: 'The reported particles were not the squeeze-bottle polymer. Scan your current package to separate bottle contact from processing evidence and compare a glass format.',
  },
  'are-nalgene-bottles-safe-microplastics': {
    product: 'your exact Nalgene bottle and lid',
    brands: 'Nalgene Tritan Renew, older polycarbonate Nalgene, stainless and glass bottles',
    payoff: 'Age and resin generation matter. Scan the molded mark and condition to avoid applying the current Tritan answer to a pre-2008 bottle.',
  },
  'microplastics-in-sparkling-water': {
    product: 'the sparkling-water format in your fridge',
    brands: 'LaCroix, Bubly, Spindrift, Waterloo, Topo Chico, Perrier',
    payoff: 'Can, PET and glass packages create different contact chains. Scan the format you buy—not just the flavor or brand—to find the easiest repeat-purchase swap.',
  },

  // ── July 17–18 2026 batch (buyer-intent + newsjack posts) ──────────────
  'do-microplastic-removal-supplements-work': {
    product: 'the products driving your daily intake',
    brands: 'bottled water, plastic food containers, chewing gum, filter pitchers',
    payoff:
      "No supplement clears stored microplastics, but cutting intake is in your control. Scan the handful of items that account for most of your daily dose.",
  },
  'foods-that-help-clear-microplastics': {
    product: 'your groceries and their packaging',
    brands: 'bottled water, canned food, plastic-wrapped produce, gum, seafood',
    payoff:
      'How your food is packaged and heated usually matters more than which vegetable you picked. Scan your kitchen to see where the dose actually comes from.',
  },
  'do-energy-drinks-have-microplastics': {
    product: 'your energy drink',
    brands: 'Celsius, Red Bull, Monster, Bang, Reign, Prime Hydration, Prime Energy',
    payoff:
      'Can or plastic bottle changes the plastic story more than the brand does. Scan yours to see which it is and the lowest-plastic way to get the same caffeine.',
  },
  'do-nespresso-pods-have-microplastics': {
    product: 'your Nespresso pod (Original or Vertuo)',
    brands: "Nespresso Original, Nespresso Vertuo, Starbucks by Nespresso, L'Or, third-party pods",
    payoff:
      "Genuine aluminum pods and plastic third-party \"compatible\" pods score very differently. Scanning yours tells you which you're actually brewing, not the brand average.",
  },
  'best-electrolyte-powders-without-microplastics': {
    product: 'your electrolyte packets and shaker bottle',
    brands: 'Liquid IV, LMNT, Nuun, Ultima, Gatorade, Powerade',
    payoff:
      "The powder isn't the issue, the packet, the water you mix into, and the shaker are. Scan them to see which part of your hydration drives the plastic.",
  },
  'does-fairlife-have-microplastics': {
    product: 'your protein shake',
    brands: 'Fairlife Core Power, Premier Protein, OWYN, Muscle Milk',
    payoff:
      'Bottle, can, or carton changes the plastic story more than the brand does. Scan yours for the lowest-plastic way to hit the same protein target.',
  },
  'microplastics-in-sous-vide': {
    product: 'your sous vide or vacuum-seal bags',
    brands: 'FoodSaver, Anova, Ziploc, reusable silicone bags',
    payoff:
      '"Food-grade" on the box doesn\'t always mean low-leaching. Scan your bags or rolls for a silicone or heat-rated swap in the same size.',
  },
  'microplastics-in-ice': {
    product: 'your ice trays and water filter',
    brands: 'silicone trays, stainless trays, plastic trays, Brita, PUR, fridge ice makers',
    payoff:
      'Ice inherits both your water and your trays. Scan both to see which one to swap first for the biggest drop.',
  },
  'microplastics-and-heart-attacks': {
    product: 'your water, bottles, and food storage',
    brands: 'bottled water, plastic food containers, filter pitchers',
    payoff:
      "You can't control the study, but you can control your kitchen. Scan the handful of items that drive most of your everyday exposure.",
  },

  // ── Coffee / pods cluster ──────────────────────────────────────────────
  'do-k-cups-release-microplastics': {
    product: 'your K-Cup pack',
    brands: 'Keurig, Green Mountain, Starbucks, Dunkin\', Peet\'s, McCafé',
    payoff:
      'Your specific pod and machine combo can score very differently from the lab averages. See which of your usual brands lands worst.',
  },
  'microplastics-in-k-cups-and-coffee-pods': {
    product: 'your K-Cup or pod brand',
    brands: 'Keurig, Nespresso, Green Mountain, Starbucks, Peet\'s, Folgers',
    payoff:
      'See your usual pod alongside the cleanest swap in the same category, ranked side-by-side.',
  },
  'nespresso-vertuo-vs-original-microplastics': {
    product: 'your Nespresso pod (Vertuo or Original)',
    brands: 'Nespresso Vertuo, Nespresso Original, Starbucks Nespresso, L\'Or, third-party pods',
    payoff:
      'Pod material varies pack to pack. Scanning yours tells you which line you\'re actually drinking from, not just the brand average.',
  },
  'microplastics-in-coffee-by-brewing-method': {
    product: 'whatever coffee you brewed this morning',
    brands: 'K-Cup, French press, drip, pour-over, Aeropress, instant',
    payoff:
      'The article ranks methods; the app ranks YOUR cup, including the filter, the kettle, and the cup itself.',
  },
  'microplastics-in-espresso-machines': {
    product: 'the pod or capsule for your espresso machine',
    brands: 'Nespresso, Lavazza, illy, Starbucks, Café Royal',
    payoff:
      'Your machine\'s pressure + capsule material combo is the actual variable. Scan the capsule, not the article.',
  },
  'does-starbucks-coffee-contain-microplastics': {
    product: 'a Starbucks cup, sleeve, or bottled drink',
    brands: 'Starbucks hot cup, cold cup, Reserve, bottled Frappuccino, K-Cup',
    payoff:
      'Different formats score very differently. Scan the one you actually order so the result is yours, not an average.',
  },
  'does-dunkin-coffee-contain-microplastics': {
    product: 'your Dunkin\' cup or pod',
    brands: 'Dunkin\' hot cup, iced cup, K-Cup, bottled cold brew',
    payoff:
      'The cup and lid material drives the score more than the coffee itself. Scan yours to know which combo you\'re actually drinking.',
  },
  'microplastics-in-iced-coffee-brands-ranked-summer-2026': {
    product: 'whichever iced coffee you bought today',
    brands: 'Starbucks, Dunkin\', Stok, Califia, La Colombe, Chameleon',
    payoff:
      'The ranking is the snapshot; the app remembers your week of iced coffees and tells you which brand actually drove your exposure up.',
  },
  'does-instant-coffee-have-microplastics': {
    product: 'your instant coffee jar or canister, and the mug',
    brands: 'Nescafé, Taster\'s Choice, Starbucks VIA, Folgers, Café Bustelo, Mount Hagen',
    payoff:
      'The jar is only one of four variables. Scan the coffee, the kettle, and the cup together to see which one is really driving your exposure.',
  },

  // ── Water-bottle cluster ───────────────────────────────────────────────
  'best-plastic-free-coffee-makers': {
    product: 'your current coffee maker and its filter pack',
    brands: 'Mr. Coffee, Cuisinart, Keurig, Moccamaster, AeroPress, Bodum',
    payoff:
      'Scan the machine you brew with every morning, the brew basket and water tank materials decide your daily hot-water plastic contact, and they vary wildly between models of the same brand.',
  },
  'does-cirkul-have-microplastics': {
    product: 'your Cirkul bottle and a Sip cartridge box',
    brands: 'Cirkul, Owala, Stanley, Hydro Flask, Gatorade Gx',
    payoff:
      'The bottle and the cartridge are different plastics with different risk profiles. Scan both to see which half of the system is worth swapping, the steel bottle upgrade changes one score but not the other.',
  },
  'are-vacuum-sealer-bags-safe-microplastics': {
    product: 'your vacuum sealer bag roll or box',
    brands: 'FoodSaver, Nesco, Anova, Wevac, Ziploc vacuum',
    payoff:
      'Bag ratings differ by brand, some rolls are rated simmer-safe, others freezer-only. Scan yours to see its polymer stack and whether your sous-vide habit matches what the bag is rated for.',
  },
  'do-air-fryers-release-microplastics': {
    product: 'your air fryer basket and its liner pack',
    brands: 'Ninja, COSORI, Philips, Instant Vortex, Chefman, plus any liner brand',
    payoff:
      'Scan the basket and the liner box separately, a pristine PTFE basket and a coated-paper liner can score on opposite ends. Find out which half of your setup needs the swap.',
  },
  'are-breast-pumps-safe-microplastics': {
    product: 'your pump parts and collection bottles',
    brands: 'Medela, Spectra, Elvie, Willow, Lansinoh, Momcozy',
    payoff:
      'Flanges, valves, and bottles are different plastics with different heat behavior. Scan each part to see which ones are worth swapping to glass or silicone first.',
  },
  'red-solo-cups-plastic-party-supplies-microplastics': {
    product: 'the party supplies you already bought',
    brands: 'Solo, Dixie, Chinet, Hefty, Great Value, Member\'s Mark',
    payoff:
      'Scan the cup sleeve, the plate pack, and the cutlery bag before the party, polystyrene, PP, and coated fiber all score very differently, and the swap can be same-day at any grocery store.',
  },
  'water-bottle-left-in-hot-car-microplastics': {
    product: 'the bottle that lives in your car cupholder',
    brands: 'Dasani, Aquafina, Fiji (single-use PET), Nalgene, CamelBak, Stanley, Hydro Flask',
    payoff:
      'The bottle that takes the most heat is the one to check. Scan it to see its polymer, then compare it to an insulated steel bottle that would stay cool instead of leaching.',
  },
  'stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics': {
    product: 'the bottle on your desk right now',
    brands: 'Stanley Quencher, Yeti Rambler, Hydro Flask, Owala FreeSip, Stojo',
    payoff:
      'Scan the exact bottle + lid combo you own. The article compares brands; the app scores your specific model with its lid material.',
  },
  'best-glass-water-bottles': {
    product: 'a glass bottle you\'re considering',
    brands: 'Lifefactory, bkr, Soma, Purifyou, Ello Pure',
    payoff:
      'Glass + silicone sleeve combos score differently from plain glass. Scan yours before you commit to a swap.',
  },
  'best-stainless-steel-water-bottles': {
    product: 'a stainless bottle you\'re considering',
    brands: 'Klean Kanteen, Hydro Flask, Yeti, Thermos, Stanley',
    payoff:
      'The interior coating (and the lid) is where most stainless bottles leak microplastics. Scan it before you buy.',
  },
  'microplastics-bottled-water-brands-ranked': {
    product: 'whatever bottled water is in your fridge',
    brands: 'Aquafina, Dasani, Fiji, Smartwater, Evian, Poland Spring',
    payoff:
      'See your usual brand alongside lower-PM alternatives, ranked by particle count per litre.',
  },
  'microplastics-in-drinking-water-by-country': {
    product: 'the bottled or filtered water you\'re drinking right now',
    brands: 'Tap, Brita, ZeroWater, LifeStraw, Berkey, Evian, Aquafina',
    payoff:
      'Country averages don\'t tell you your kitchen score. Scan your actual filter and bottle.',
  },
  'microplastics-water-filters-compared': {
    product: 'the filter you currently use',
    brands: 'Brita Standard, Brita Elite, ZeroWater, LifeStraw, Berkey, PUR',
    payoff:
      'See yours next to the next-tier-up option so the upgrade decision is informed, not guessed.',
  },
  'do-brita-filters-remove-microplastics': {
    product: 'your Brita (or whatever pitcher you own) + the bottle you pour into',
    brands: 'Brita Standard, Brita Elite, ZeroWater, Clearly Filtered, PUR',
    payoff:
      'A filter only helps if the bottle you pour into doesn\'t re-add plastic. Scan the pitcher and your daily bottle together.',
  },

  // ── Food storage / pantry ──────────────────────────────────────────────
  'do-ziploc-bags-release-microplastics': {
    product: 'whatever plastic bag is in your pantry',
    brands: 'Ziploc Freezer, Ziploc Slider, Hefty, Glad, Stasher (silicone)',
    payoff:
      'Bag thickness + freezer-vs-fridge use changes the score. Scan the box you actually have so the swap recommendation is real.',
  },
  'microplastics-in-canned-food': {
    product: 'a can from your pantry',
    brands: 'Campbell\'s, Progresso, Bumble Bee, Eden Organic, Amy\'s',
    payoff:
      'BPA-free linings vary brand to brand. Scan the can to see whether yours is one of the cleaner-lined options.',
  },
  'microplastics-in-plastic-containers': {
    product: 'your most-used food container',
    brands: 'Rubbermaid, Tupperware, Pyrex with plastic lid, Glasslock, Stasher',
    payoff:
      'The lid is usually the worst-scoring part. Scan yours to know whether to swap the whole set or just the lids.',
  },
  'best-plastic-free-food-storage': {
    product: 'a container you\'re considering swapping to',
    brands: 'Pyrex, Glasslock, Stasher, Bee\'s Wrap, Anchor Hocking',
    payoff:
      'Even "glass" containers often ship with plastic lids. Scan before you switch so the swap is actually cleaner.',
  },

  // ── Baby / pregnancy ───────────────────────────────────────────────────
  'microplastics-from-breast-pump-parts': {
    product: 'your pump bottle or flange',
    brands: 'Medela, Spectra, Elvie, Willow, Lansinoh, Haakaa',
    payoff:
      'Pump material varies model to model, and within a model by part. Scan each part so your daily pumping routine is actually safer.',
  },
  'best-baby-bottles-microplastic-free': {
    product: 'the baby bottle you\'re feeding from today',
    brands: 'Dr. Brown\'s glass, Philips Avent glass, Comotomo, Lansinoh, Pigeon',
    payoff:
      'Glass-vs-plastic is the obvious axis; nipple silicone grade is the hidden one. Scan both so the choice is informed.',
  },
  'microplastics-in-breast-milk': {
    product: 'whatever storage bag or bottle you pump into',
    brands: 'Lansinoh storage bags, Medela bottles, Kiinde, Dr. Brown\'s',
    payoff:
      'Storage material is the biggest controllable source of microplastic transfer. Scan yours so you can swap if needed.',
  },
  'microplastics-in-disposable-diapers': {
    product: 'the diaper you\'re currently using',
    brands: 'Pampers, Huggies, Honest, Hello Bello, Coterie, Dyper',
    payoff:
      'Inner-layer materials vary across "natural" brands. Scan yours to see how it actually scores.',
  },

  // ── Dead-engagement-page rescue: GA4 shows these pages get views but
  //    0–6% engagement rate. Adding a specific scan prompt gives the
  //    bouncing reader one concrete next action instead of a wall of
  //    generic copy. Low expectation on lift, but the entries are
  //    cheap to write. ──────────────────────────────────────────────────
  'microplastics-in-food': {
    product: 'one packaged item from your pantry right now',
    brands: 'Canned soup, frozen meal, pouch sauce, plastic-wrapped meat, dairy tub',
    payoff:
      'Food category averages aren\'t your food. Scan what you actually eat to get your real per-meal microplastic load.',
  },
  'microplastics-in-fruits-and-vegetables': {
    product: 'the produce sitting in your fridge',
    brands: 'Pre-cut bagged salad, berries in clamshells, frozen veg pouches, plastic-wrapped cucumber',
    payoff:
      'Most produce microplastics come from packaging, not the food itself. Scan the packaging, that\'s the real lever.',
  },

  // ── Other ──────────────────────────────────────────────────────────────
  'microplastics-in-tea-bags': {
    product: 'the tea bag you steeped this morning',
    brands: 'Lipton, Tetley, Twinings, Yogi, Pukka, Numi, paper-only loose leaf',
    payoff:
      '"Silken" pyramid bags release the most. Scan yours to know if you need to switch to paper or loose-leaf.',
  },
  'microplastics-in-milk-and-dairy': {
    product: 'the milk carton or yogurt cup in your fridge',
    brands: 'Horizon, Organic Valley, Fairlife, Chobani, Oikos, Siggi\'s',
    payoff:
      'Carton liners vary supplier to supplier. Scan yours so you know which one you\'re actually drinking from.',
  },
  'how-to-test-for-microplastics-at-home': {
    product: 'the bottle, container, or appliance you most want to test',
    brands: 'Any product you use daily',
    payoff:
      'Lab kits give one number per sample. The app gives a running score for everything you scan, no kit required.',
  },
  'how-long-do-microplastics-stay-in-your-body': {
    product: 'the products driving your daily intake',
    brands: 'Bottled water, K-Cups, takeout containers, tea bags, cosmetics',
    payoff:
      'You can\'t shrink the half-life, but you can shrink the daily input. Scan your top sources to see what dominates your exposure.',
  },

  // ── August 6–10 2026 batch: the two new brand-verdict clusters ──────────
  // Every article in this batch gets an entry deliberately. In the 28d to
  // 2026-08-09 only 568 users ever reached the scan demo, because it rides on
  // FirstScanCallout and FirstScanCallout only renders where a prompt exists
  // (48 entries against 254 posts). Brand-verdict pages are exactly where the
  // prompt lands best: the reader is holding the product while reading.
  'protein-shakes-ranked-microplastics-phthalates': {
    product: 'the shake in your fridge',
    brands: 'Fairlife Core Power, Premier Protein, Muscle Milk, Orgain, OWYN, SlimFast',
    payoff:
      'Not one brand in this category publishes what its container is made of. Scanning the pack is currently the only way to find out what yours is.',
  },
  'does-premier-protein-have-microplastics': {
    product: 'your Premier Protein carton or bottle',
    brands: 'Premier Protein shakes, Protein Sodas, Coffeehouse, Indulgence',
    payoff:
      'Premier sells the same shake in a carton and a plastic bottle. Scan the one you actually buy to see which format you have been picking up.',
  },
  'does-muscle-milk-have-microplastics': {
    product: 'your Muscle Milk bottle',
    brands: 'Muscle Milk Protein Shake, Muscle Milk Pro, 11oz and 14oz',
    payoff:
      'Cytosport publishes neither the bottle material nor an ingredient list, so the pack in your hand tells you more than the brand site does.',
  },
  'drugstore-skincare-microplastics-audited': {
    product: 'the moisturiser, cleanser and SPF on your shelf',
    brands: 'CeraVe, Cetaphil, Neutrogena, La Roche-Posay, Aveeno, Olay',
    payoff:
      'We audited 290 formulas, but not every SKU or region. Scan yours to check it against the same solid-polymer list.',
  },
  'does-cerave-have-microplastics': {
    product: 'your CeraVe bottle or tub',
    brands: 'Moisturizing Cream, Daily Moisturizing Lotion, Foaming Cleanser, SPF lotions',
    payoff:
      'Only four CeraVe formulas contain a solid polymer particle, and one is an SPF. Scan yours to confirm which side of that line it sits on.',
  },
  'does-cetaphil-have-microplastics': {
    product: 'your Cetaphil cleanser or SPF moisturiser',
    brands: 'Gentle Skin Cleanser, Moisturizing Lotion, Daily Facial Moisturizer SPF 15, DermaControl',
    payoff:
      'Three of the five Cetaphil hits are SPF products. Scan the one in your bag — that is where the polymer clusters.',
  },
  'can-you-microwave-ziploc-bags': {
    product: 'the bags, tubs and wraps you reheat in',
    brands: 'Ziploc, Glad, Hefty, Great Value, Tupperware',
    payoff:
      'Microwave-safe is a melting-and-migration rating, not a particle one. Scan what you actually reheat in to see which items should never meet heat.',
  },
  'nanoplastics-placenta-hormones-study-2026': {
    product: 'the packaging you handle most while pregnant',
    brands: 'Takeaway containers, cling film, PET water bottles, foam cups',
    payoff:
      'Polystyrene, PVC and PET were three of the four polymers that crossed. Scan your kitchen to find which of them you meet daily.',
  },

  '7-day-microplastic-detox-challenge': {
    product: 'whichever product you suspect is driving your daily intake',
    brands: 'Bottled water, K-Cups, takeout containers, microwave plastic, tea bags',
    payoff:
      'A real detox only works if you reduce input. Scan your top 5 daily-use items and the app shows which one to swap first.',
  },
  'can-you-microwave-solo-cups': {
    product: 'the cups and lids you actually drink hot things from',
    brands: 'Solo, Dixie, Hefty, Chinet, coffee-shop cups and lids',
    payoff:
      'Solo publishes its resins; almost nobody else does. Scan the cup in your hand to see whether it is polystyrene, PET or paper — and whether its lid is the worst part.',
  },
  'does-neutrogena-have-microplastics': {
    product: 'your Neutrogena moisturiser, cleanser or SPF',
    brands: 'Hydro Boost, Rapid Wrinkle Repair, Ultra Sheer, Deep Clean, Body Clear',
    payoff:
      'Twelve of the affected products are Hydro Boost and most of the rest of the range is clean, so which line you own decides the answer. Scan the bottle rather than the brand.',
  },
  'can-you-microwave-rubbermaid': {
    product: 'the container you reheat leftovers in',
    brands: 'Rubbermaid Brilliance, TakeAlongs, Easy Find Lids, Tupperware, Ziploc containers',
    payoff:
      'Brilliance is capped at 165°F and TakeAlongs at 212°F, and neither prints that on the tub. Scan yours to see which material you actually own.',
  },
  'do-contact-lenses-shed-microplastics': {
    product: 'the case and solution bottle on your bathroom shelf',
    brands: 'Acuvue, Dailies, Biofinity, Air Optix, Precision1, Opti-Free, Renu, Biotrue',
    payoff:
      'The 2026 study put the case, the solution and the blister pack above the lens itself. Those three are the only parts of the routine you can actually change — scan them.',
  },
  'do-gel-and-acrylic-nails-shed-microplastics': {
    product: 'the polish, base coat or top coat in your drawer',
    brands: 'OPI, Essie, Sally Hansen, Olive & June, CND Shellac, Gelish, Static Nails',
    payoff:
      'Acrylates copolymer, styrene/acrylates copolymer and PET glitter are the film formers doing the work, and "10-free" says nothing about any of them. Scan the ingredient list.',
  },
  'think-dirty-vs-yuka': {
    product: 'whatever a hazard scanner just gave a clean score to',
    brands: 'CeraVe, The Ordinary, Neutrogena, Cetaphil, Drunk Elephant, e.l.f.',
    payoff:
      'A high Yuka score means nothing on their hazard list — not nothing plastic. Acrylates copolymer and nylon-12 pass both apps untouched. Scan the same label for polymers.',
  },
  'microplastic-testing': {
    product: 'the filter jug, bottled water or food packaging you already own',
    brands: 'Brita, ZeroWater, PUR, LifeStraw, Aquasana, Berkey',
    payoff:
      'A lab test has a size floor and costs real money. Scanning the products that put plastic into your water in the first place does not — start with whatever is on your counter.',
  },
}

export function getFirstScanPrompt(slug: string): FirstScanPrompt | undefined {
  return FIRST_SCAN_PROMPTS[slug]
}
